import { Clock } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useGuardContext } from '@/hooks/useGuardContext';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * ShiftHeader — Displays shift start time and elapsed duration in the nav header.
 * Updates every minute to show the running shift timer.
 * Requirements: 9.3
 */
export function ShiftHeader() {
  const { t } = useTranslation();
  const { activeShift } = useGuardContext();
  const elapsed = useShiftElapsed(activeShift?.startedAt ?? null);

  if (!activeShift) return null;

  const startTime = new Date(activeShift.startedAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className="flex items-center gap-2 rounded-md bg-[var(--bg-surface)] px-3 py-1.5 border border-[var(--border-subtle)]"
      aria-label={t('guard.header.shiftActive')}
    >
      <Clock className="size-3.5 text-[var(--status-green-text)]" />
      <span className="text-xs text-[var(--text-secondary)]">
        {t('guard.shift.startedAt')} {startTime}
      </span>
      <span className="text-[var(--border-default)]">·</span>
      <span className="font-mono text-xs font-medium text-[var(--text-primary)] tabular-nums">
        {elapsed}
      </span>
    </div>
  );
}

// ─── Shift elapsed timer hook ────────────────────────────────────────────────

function formatElapsed(startIso: string): string {
  const elapsed = Date.now() - new Date(startIso).getTime();
  const hours = Math.floor(elapsed / 3_600_000);
  const minutes = Math.floor((elapsed % 3_600_000) / 60_000);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function useShiftElapsed(startIso: string | null): string {
  const [elapsed, setElapsed] = useState(startIso ? formatElapsed(startIso) : '00:00');

  useEffect(() => {
    if (!startIso) {
      setElapsed('00:00');
      return;
    }
    setElapsed(formatElapsed(startIso));
    const interval = setInterval(() => setElapsed(formatElapsed(startIso)), 60_000);
    return () => clearInterval(interval);
  }, [startIso]);

  return elapsed;
}
